import { useContext, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { Grid } from "@mui/material";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { LocaleContext } from "../../../../routing/LangRouter.js";

//import customized hooks
import { useFormValidations } from "../../../../hooks/useFormValidations.js";

//import constants
import BASE_URL from "../../../../constants/baseUrls.js";

//import utils
import { fixNumbers, navigateToLoginPage } from "../../../../utils/utility.js";

//import general components
import BaseTextField from "../../../common/base-text-field/base-text-field.jsx";
import AutoCompleteComponent from "../../../common/base-select/autocomplete-component.js.jsx";
import { BaseButton } from "../../../common/base-button/base-button.jsx";

function BankCardForm({ setIsCardList }) {
    //general variables
    const navigate = useNavigate();
    const location = useLocation();
    const { t } = useTranslation();
    const { locale } = useContext(LocaleContext);
    const [loading, setLoading] = useState(false);
    const accessToken = localStorage.getItem("accessToken");
    const [isFocused, setIsFocused] = useState({});

    //request to get bank list
    const [bankList, setBankList] = useState([]);
    const [bankSearchValue, setBankSearchValue] = useState("");
    const getBankList = async () => {
        const response = await axios.get(`${BASE_URL}/basic/bank/list/`);
        setBankList(response.data.results);
    };
    useEffect(() => {
        getBankList();
    }, []);

    //form list
    const formList = [
        { id: 1, label: t("cardNumber"), name: "card_number", fieldType: "number", type: "input" },
        { id: 2, label: t("accountNumber"), name: "account_number", fieldType: "number", type: "input" },
        { id: 3, label: t("shebaNumber"), name: "sheba_number", fieldType: "number", type: "input" },
        { id: 4, label: t("Form Messages.Bank name"), name: "bank", type: "select", options: bankList, setInputValue: setBankSearchValue },
    ];

    //handle form submit
    const {
        register,
        handleSubmit,
        getValues,
        control,
        formState: { errors },
    } = useForm({
        mode: "onChange",
        delayError: 500,
    });

    //handle form submit
    const handleRegistration = async data => {
        // Normalize and clean card number
        const normalizedCardNumber = fixNumbers(data.card_number);
        const cleanedCardNumber = normalizedCardNumber.replace(/\s/g, "");

        // Prepare form data with normalized values
        const formData = new FormData();
        formData.append("bank", data.bank.id);
        formData.append("card_number", cleanedCardNumber);
        formData.append("account_number", fixNumbers(data.account_number));
        formData.append("sheba_number", fixNumbers(data.sheba_number));

        setLoading(true);
        try {
            const response = await axios.post(`${BASE_URL}/account/user/bank_account/`, formData, {
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                },
            });
            toast.success(response.data.message, { toastId: "custom-id-yes" });
        } catch (error) {
            if (error.response.status === 401) {
                localStorage.clear();
                navigateToLoginPage(navigate, locale, location);
            } else {
                toast.error(t("Toast Messages.Error Occurred"), { toastId: "custom-id-yes" });
                setIsCardList(true);
            }
        } finally {
            setLoading(false);
            setIsCardList(true);
        }
    };

    const handleError = error => console.log(error);

    //form validations
    const formValidations = useFormValidations([]);

    return (
        <Grid className="bank-account-information__bank-card-form" container item xs={12}>
            <Grid
                className="bank-account-information__form"
                component="form"
                container
                item
                xs={12}
                rowGap={4}
                justifyContent="space-between"
                onSubmit={handleSubmit(handleRegistration, handleError)}
            >
                {formList.map(field => {
                    return (
                        <Grid key={field?.id} container item md={3.8}>
                            {field.type === "input" && (
                                <BaseTextField
                                    dir="ltr"
                                    label={field.label}
                                    radius="10px"
                                    name={field.name}
                                    type={field.fieldType}
                                    focused={!!getValues(`${field.name}`) || isFocused[field.name]}
                                    onFocus={() => setIsFocused(prev => ({ ...prev, [field.name]: true }))}
                                    register={{ ...register(field.name, formValidations?.[field?.name]) }}
                                    error={errors?.[field?.name]?.message}
                                />
                            )}

                            {field.type === "select" && (
                                <AutoCompleteComponent
                                    label={field.label}
                                    options={field.options}
                                    name={field.name}
                                    defaultValue={getValues(`${field.name}`)}
                                    setInputValue={field.setInputValue}
                                    control={control}
                                    register={formValidations?.[field?.name]}
                                    errors={errors?.[field?.name]?.message}
                                    width="auto"
                                />
                            )}
                        </Grid>
                    );
                })}

                <Grid container item md={2.5}>
                    <BaseButton text={t("submit")} color="primary" size="large" radius="radius-10" loading={loading} />
                </Grid>
            </Grid>
        </Grid>
    );
}

export default BankCardForm;
